import * as React from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ParticleField from './ParticleField';
import StatCounter from './StatCounter';

const ROTATING = ['cloud platforms', 'mobile apps', 'desktop software', 'AI-driven tools', 'secure systems'];

function RotatingWord() {
  const [i, setI] = React.useState(0);
  const reduce = useReducedMotion();

  React.useEffect(() => {
    if (reduce) return undefined;
    const id = setInterval(() => setI((p) => (p + 1) % ROTATING.length), 2400);
    return () => clearInterval(id);
  }, [reduce]);

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        position: 'relative',
        minWidth: { xs: '100%', sm: 'auto' },
      }}
    >
      <AnimatePresence mode="wait">
        <motion.span
          key={i}
          initial={reduce ? false : { y: '0.6em', opacity: 0, filter: 'blur(6px)' }}
          animate={{ y: 0, opacity: 1, filter: 'blur(0px)' }}
          exit={reduce ? {} : { y: '-0.6em', opacity: 0, filter: 'blur(6px)' }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          style={{ display: 'inline-block' }}
          className="fx-gradient-text"
        >
          {ROTATING[i]}
        </motion.span>
      </AnimatePresence>
    </Box>
  );
}

/** A button that subtly leans toward the cursor. */
function MagneticButton({ children, sx, strength = 0.4, ...props }) {
  const ref = React.useRef(null);
  const reduce = useReducedMotion();

  const handleMove = (e) => {
    if (reduce || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    ref.current.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
  };
  const reset = () => {
    if (ref.current) ref.current.style.transform = 'translate(0, 0)';
  };

  return (
    <Button
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      sx={{ transition: 'transform 0.25s cubic-bezier(0.22,1,0.36,1)', ...sx }}
      {...props}
    >
      {children}
    </Button>
  );
}

export default function Hero({ scrollToSection, servicesRef, contactUsRef }) {
  const reduce = useReducedMotion();

  const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
  };
  const item = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        minHeight: { xs: 'auto', md: '100vh' },
        display: 'flex',
        alignItems: 'center',
        pt: { xs: 16, md: 12 },
        pb: { xs: 10, md: 8 },
      }}
    >
      {/* Base gradient wash */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: (t) =>
            t.palette.mode === 'dark'
              ? 'radial-gradient(1200px 600px at 50% -10%, hsla(210,98%,30%,0.35), transparent 60%), radial-gradient(900px 500px at 85% 20%, hsla(265,90%,45%,0.25), transparent 60%)'
              : 'radial-gradient(1200px 600px at 50% -10%, hsla(210,98%,75%,0.4), transparent 60%), radial-gradient(900px 500px at 85% 20%, hsla(265,90%,80%,0.35), transparent 60%)',
        }}
      />

      {/* Grid overlay */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: (t) =>
            `linear-gradient(${
              t.palette.mode === 'dark' ? 'hsla(210,40%,60%,0.06)' : 'hsla(210,40%,40%,0.06)'
            } 1px, transparent 1px), linear-gradient(90deg, ${
              t.palette.mode === 'dark' ? 'hsla(210,40%,60%,0.06)' : 'hsla(210,40%,40%,0.06)'
            } 1px, transparent 1px)`,
          backgroundSize: '54px 54px',
          maskImage: 'radial-gradient(circle at 50% 40%, black, transparent 78%)',
          WebkitMaskImage: 'radial-gradient(circle at 50% 40%, black, transparent 78%)',
        }}
      />

      {/* Floating aurora blobs */}
      {!reduce &&
        [
          { top: '12%', left: '8%', size: 320, c: '210,98%,55%', dur: '9s' },
          { top: '55%', left: '78%', size: 380, c: '265,90%,62%', dur: '11s' },
          { top: '70%', left: '12%', size: 260, c: '190,95%,55%', dur: '10s' },
        ].map((b, idx) => (
          <Box
            key={idx}
            aria-hidden
            sx={{
              position: 'absolute',
              top: b.top,
              left: b.left,
              width: b.size,
              height: b.size,
              borderRadius: '50%',
              background: `radial-gradient(circle, hsla(${b.c},0.5), transparent 70%)`,
              filter: 'blur(50px)',
              animation: `fx-pulse-glow ${b.dur} ease-in-out infinite`,
              pointerEvents: 'none',
            }}
          />
        ))}

      {/* Interactive constellation */}
      <ParticleField />

      <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
        <motion.div variants={container} initial="hidden" animate="show">
          {/* Availability badge */}
          <motion.div variants={item}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                mb: 3.5,
                borderRadius: 999,
                border: '1px solid',
                borderColor: 'divider',
                backdropFilter: 'blur(10px)',
                bgcolor: (t) =>
                  t.palette.mode === 'dark' ? 'hsla(220,30%,12%,0.5)' : 'hsla(0,0%,100%,0.6)',
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'hsl(140,70%,45%)',
                  boxShadow: '0 0 0 0 hsla(140,70%,45%,0.7)',
                  animation: reduce ? 'none' : 'fx-pulse-glow 2s ease-in-out infinite',
                }}
              />
              <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
                Available for new projects · 2026
              </Typography>
            </Box>
          </motion.div>

          {/* Headline */}
          <motion.div variants={item}>
            <Typography
              component="h1"
              sx={{
                fontFamily: '"Sora","Inter",sans-serif',
                fontWeight: 800,
                lineHeight: 1.05,
                letterSpacing: -1,
                fontSize: { xs: '2.6rem', sm: '3.6rem', md: '4.5rem' },
                color: 'text.primary',
              }}
            >
              We engineer
              <br />
              exceptional{' '}
              <RotatingWord />
            </Typography>
          </motion.div>

          {/* Subtitle */}
          <motion.div variants={item}>
            <Typography
              sx={{
                mt: 3,
                mx: 'auto',
                maxWidth: 640,
                fontSize: { xs: '1.05rem', md: '1.2rem' },
                lineHeight: 1.7,
                color: 'text.secondary',
              }}
            >
              FalconXcellence is a software engineering studio delivering scalable, secure,
              high-performance products — from first line of code to production and beyond.
            </Typography>
          </motion.div>

          {/* CTAs */}
          <motion.div variants={item}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="center"
              sx={{ mt: 4.5 }}
            >
              <MagneticButton
                variant="contained"
                size="large"
                endIcon={<ArrowForwardRoundedIcon />}
                onClick={() => scrollToSection(contactUsRef, false)}
                sx={{
                  px: 3.5,
                  py: 1.4,
                  fontWeight: 700,
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  color: '#fff !important',
                  border: 'none !important',
                  backgroundColor: 'hsl(230,90%,55%) !important',
                  backgroundImage:
                    'linear-gradient(100deg, hsl(210,98%,52%), hsl(265,90%,60%)) !important',
                  boxShadow: '0 10px 30px hsla(220,90%,50%,0.4) !important',
                  '&:hover': {
                    backgroundImage:
                      'linear-gradient(100deg, hsl(210,98%,58%), hsl(265,90%,66%)) !important',
                    boxShadow: '0 14px 40px hsla(220,90%,50%,0.55) !important',
                  },
                }}
              >
                Start your project
              </MagneticButton>
              <MagneticButton
                variant="outlined"
                size="large"
                onClick={() => scrollToSection(servicesRef)}
                strength={0.25}
                sx={{
                  px: 3.5,
                  py: 1.4,
                  fontWeight: 600,
                  borderRadius: 2.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  borderColor: 'divider',
                  color: 'text.primary',
                  backdropFilter: 'blur(8px)',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'action.hover',
                  },
                }}
              >
                Explore services
              </MagneticButton>
            </Stack>
          </motion.div>

          {/* Stats */}
          <motion.div variants={item}>
            <Box
              sx={{
                mt: { xs: 6, md: 9 },
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                gap: { xs: 3, sm: 2 },
                maxWidth: 620,
                mx: 'auto',
                pt: 4,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <StatCounter value={50} suffix="+" label="Projects delivered" />
              <StatCounter value={12} suffix="+" label="Years combined" />
              <StatCounter value={4} label="Platforms mastered" />
              <StatCounter value={99} suffix="%" label="Client retention" />
            </Box>
          </motion.div>
        </motion.div>
      </Container>

      {/* Scroll indicator */}
      {!reduce && (
        <Box
          onClick={() => scrollToSection(servicesRef)}
          aria-hidden
          sx={{
            position: 'absolute',
            bottom: 26,
            left: '50%',
            transform: 'translateX(-50%)',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            zIndex: 2,
            color: 'text.secondary',
          }}
        >
          <Box
            sx={{
              width: 26,
              height: 42,
              borderRadius: 999,
              border: '2px solid',
              borderColor: 'divider',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 7,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 4,
                height: 8,
                borderRadius: 999,
                bgcolor: 'primary.main',
                animation: 'fx-scroll-dot 1.8s ease-in-out infinite',
              }}
            />
          </Box>
          <KeyboardArrowDownRoundedIcon fontSize="small" />
        </Box>
      )}
    </Box>
  );
}
